<template>
  <section class="mx-3 lg:mx-0 max-w-screen-2xl">
    <div class="md:mx-14 pt-4 mx-5">
      <div
        class="flex justify-between items-center text-root-1 font-normal text-xs md:text-sm"
      >
        <div class="text-root-2 py-4 text-xl font-bold">
          Withdrawal Accounts
        </div>
        <button
          @click="
            isUserKindViewOnly
              ? () => {}
              : openDrawer('showAddWithdrawalDrawer')
          "
          :disabled="!withdrawalAccess.includes(user.kind)"
          :class="isUserKindViewOnly ? 'opacity-60 cursor-not-allowed' : ''"
          class="bg-primary cursor-pointer py-3.5 w-2/3 md:w-1/3 lg:w-1/5 rounded-lg text-center flex justify-center gap-x-1.5 items-center text-white font-normal"
        >
          <span class="text-xl pr-2"><plus-icon /></span> Add withdrawal
          Accounts
        </button>
      </div>

      <div
        class="flex lg:items-center items-start lg:flex-row flex-col gap-y-3 gap-x-6 my-6 pb-4 lg:pb-0 border-b"
      >
        <div
          @click="makeActive($transactionType.BANK_TRANSFER)"
          :class="[
            isActive($transactionType.BANK_TRANSFER)
              ? 'border-b-2 text-primary font-bold border-primary'
              : 'text-fg-muted font-medium',
            'cursor-pointer pb-1 lg:pb-2.5 lg:text-13px text-xs max-w-xl'
          ]"
        >
          Fiat Accounts
        </div>
        <div
          v-if="tenantPermission('asset-access')"
          @click="makeActive($transactionType.ASSET_TRANSFER)"
          :class="[
            isActive($transactionType.ASSET_TRANSFER)
              ? 'border-b-2 text-primary font-bold border-primary'
              : 'text-fg-muted font-medium',
            'cursor-pointer pb-1 lg:pb-2.5 lg:text-13px text-xs max-w-xl'
          ]"
        >
          Asset Accounts
        </div>
      </div>

      <div
        v-if="isFiatAccount"
        class="w-full border rounded-lg text-root-1 font-normal text-xs md:text-sm px-4"
      >
        <beneficiaries-loader v-if="isLoading" />

        <div v-else-if="fiatAccounts.length > 0">
          <div
            v-for="(withdrawalMethod, index) in fiatAccounts"
            :key="index"
            class="flex p-4 gap-4 border-bottom"
          >
            <span class="uppercase text-center justify-center">
              <el-avatar
                :size="60"
                class="font-semibold flex text-lg items-center justify-center bg-white border border-gray-11 text-primary"
              >
                {{ getNameInitials(getAccountName(withdrawalMethod)) }}
              </el-avatar>
            </span>

            <div class="w-full flex items-center justify-between">
              <div>
                <p class="font-bold text-lg text-root-2">
                  {{ getAccountName(withdrawalMethod) }}
                </p>
                <div
                  class="flex flex-col md:flex-row md:gap-x-3 gap-y-3 md:items-center items-start text-fg-muted text-sm mt-2"
                >
                  <p
                    class="flex flex-col md:flex-row md:divide-x-2 gap-y-2 md:gap-x-4"
                  >
                    <span>
                      {{ getBankName(withdrawalMethod) }}
                    </span>
                    <span class="pl-0 md:pl-4">
                      {{ getAccountNumber(withdrawalMethod) }}
                    </span>
                    <span
                      class="flex flex-row items-center gap-x-2 pl-0 md:pl-4 text-fg-muted md:mr-1.5 md:ml-3"
                    >
                      <currency-icons
                        :currency="
                          getCurrencyCountry(getCurrency(withdrawalMethod))
                        "
                      />
                      <span class="font-bold">
                        {{ getCurrency(withdrawalMethod) }}
                      </span>
                    </span>
                  </p>
                </div>
              </div>

              <div class="flex gap-3 md:items-center">
                <div
                  class="text-primary rounded-lg px-2.5 py-2 border flex text-center justify-center"
                  @click="openWithdrawalDetailsDrawer(withdrawalMethod)"
                >
                  <span class="cursor-pointer"><eye-icon /></span>
                </div>
                <div
                  class="text-primary rounded-lg px-3 py-2.5 border border-error-default flex text-center justify-center"
                  @click="confirmDeleteWithdrawalMethod(withdrawalMethod.id)"
                >
                  <span class="cursor-pointer"><delete-icon /></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <accounts-empty-state v-else use="withdrawal-method" />
      </div>

      <div
        v-if="isAssetAccount"
        class="w-full border rounded-lg text-root-1 font-normal text-xs md:text-sm px-4"
      >
        <beneficiaries-loader v-if="isLoading" />

        <div
          v-else-if="
            assetAccounts.length > 0 && tenantPermission('asset-access')
          "
        >
          <div
            v-for="(withdrawalMethod, index) in assetAccounts"
            :key="index"
            class="border-bottom p-4"
          >
            <div class="w-full flex items-center gap-5">
              <asset-icon class="w-8 h-8" />

              <div class="w-full flex items-center justify-between">
                <div>
                  <p class="font-bold text-lg text-root-2">
                    {{ getAssetName(withdrawalMethod) }}
                  </p>

                  <div
                    class="flex md:flex-row flex-col md:gap-x-5 gap-y-2.5 md:items-center items-start"
                  >
                    <p
                      class="flex items-center text-fg-muted text-sm mt-2 lg:mt-0"
                    >
                      Address:
                      <span class="pl-1 flex items-center text-root-2">
                        {{ maskID(getWalletAddress(withdrawalMethod)) }}
                      </span>

                      <span
                        class="cursor-pointer mb-1 ml-2"
                        v-clipboard:copy="getWalletAddress(withdrawalMethod)"
                        v-clipboard:success="onCopy"
                        v-clipboard:error="onError"
                      >
                        <copy-icon />
                      </span>
                    </p>

                    <p
                      class="flex items-center gap-x-1.5 md:ml-4"
                      v-if="withdrawalMethod.complianceStatus"
                    >
                      Compliance:
                      <span
                        class="text-xs text-center font-normal rounded-full py-2 capitalize px-3"
                        :class="statusClass(withdrawalMethod.complianceStatus)"
                      >
                        {{ withdrawalMethod.complianceStatus || 'N/A' }}
                      </span>
                    </p>
                  </div>
                </div>

                <div class="flex gap-3 md:items-center">
                  <div
                    class="text-primary rounded-lg px-2.5 py-2 border flex text-center justify-center"
                    @click="openWithdrawalDetailsDrawer(withdrawalMethod)"
                  >
                    <span class="cursor-pointer"><eye-icon /></span>
                  </div>
                  <div
                    class="text-primary rounded-lg px-3 py-2.5 border border-error-default flex text-center justify-center"
                    @click="confirmDeleteWithdrawalMethod(withdrawalMethod.id)"
                  >
                    <span class="cursor-pointer"><delete-icon /></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <accounts-empty-state v-else use="withdrawal-method" />
      </div>
    </div>

    <!-- drawers -->
    <withdrawal-details-drawer
      v-if="showDrawer"
      :showDrawer="showDrawer"
      @closeDrawer="closeDrawer('showDrawer')"
      :currentWithdrawalDetails="currentWithdrawalDetails"
      :activeTab="activeTab"
      @handleDeleteWithdrawalMethod="handleDeleteWithdrawalMethod"
    />
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="withdrawalState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeDialog('showVerifyAuthDialog')"
      @verifiedUser="createWithdrawalMethod"
      @deleteWithdrawalMethod="deleteWithdrawalMethod"
    />
    <attention-dialog
      v-if="showDeleteWithdrawalDialog"
      :showDialog="showDeleteWithdrawalDialog"
      @closeDialog="closeDialog('showDeleteWithdrawalDialog')"
      @verifyDeleteWithdrawal="verifyAuth('deleteWithdrawalMethod')"
      :mode="mode"
      :message="message"
    />
    <withdrawal-account-drawer
      v-if="showAddWithdrawalDrawer"
      :showDrawer="showAddWithdrawalDrawer"
      @proceedToAddWithdrawal="proceedToAddWithdrawal"
      @closeDrawer="closeDrawer('showAddWithdrawalDrawer')"
    />
  </section>
</template>

<script>
import { WITHDRAWAL_ACCESS } from '@/utils/constants'
import {
  GetCurrencyCountry,
  MaskWalletAddressTwo,
  StatusClass
} from '@/utils/helpers.js'
import tenant from '@/mixins/tenant.js'
import withdrawal from '@/mixins/withdrawal.js'
import notification from '@/mixins/notification.js'
import bulkPayout from '@/mixins/bulkPayout.js'
import userKinds from '@/mixins/userKinds.js'
export default {
  mixins: [tenant, withdrawal, notification, bulkPayout, userKinds],
  data () {
    return {
      activeTab: this.$transactionType.BANK_TRANSFER,
      authState: '',
      isLoading: false,
      showDrawer: false,
      showAddWithdrawalDrawer: false,
      showVerifyAuthDialog: false,
      showDeleteWithdrawalDialog: false,
      withdrawalDetails: null,
      twoFaCode: null,
      withdrawalId: null,
      withdrawalState: '',
      withdrawalAccess: WITHDRAWAL_ACCESS,
      meta: {},
      currentWithdrawalDetails: {},
      mode: '',
      message: 'Are you sure you want to delete this account'
    }
  },
  methods: {
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    statusClass (status) {
      return StatusClass(status)
    },
    maskID (val) {
      return MaskWalletAddressTwo(val)
    },
    isActive (tab) {
      if (tab === this.activeTab) {
        return true
      } else {
        return false
      }
    },
    async makeActive (tab) {
      this.activeTab = tab
      await this.getWithdrawalMethods()
    },
    openWithdrawalDetailsDrawer (withdrawalMethod) {
      this.currentWithdrawalDetails = withdrawalMethod
      this.showDrawer = true
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    handleDeleteWithdrawalMethod () {
      this.confirmDeleteWithdrawalMethod(this.currentWithdrawalDetails.id)
      this.closeDrawer('showDrawer')
    },
    confirmDeleteWithdrawalMethod (id) {
      if (!this.withdrawalAccess.includes(this.user.kind)) {
        this.$message({
          showClose: true,
          message: 'No access to delete withdrawal option',
          type: 'info'
        })
        return
      }
      this.withdrawalId = id
      this.mode = 'deleteWithdrawalMethod'
      this.showDeleteWithdrawalDialog = true
    },
    closeDialog (arg) {
      this[arg] = false
    },
    proceedToAddWithdrawal (payload) {
      this.withdrawalDetails = payload
      this.verifyAuth('createWithdrawal')
    },
    verifyAuth (withdrawalState) {
      this.showVerifyAuthDialog = true
      this.withdrawalState = withdrawalState
    },
    deleteWithdrawalMethod (twoFaCode) {
      const meta = {
        withdrawalId: this.withdrawalId,
        twoFaCode: twoFaCode.code
      }
      const vm = this
      this.$store
        .dispatch('withdrawal/deleteWithdrawalMethod', meta)
        .then(() => {
          this.showVerifyAuthDialog = false
          this.getWithdrawalMethods()
          this.$swal({
            icon: 'success',
            title: 'Withdrawal method deleted successfully',
            iconColor: this.$primaryColor,
            allowOutsideClick: false
          })
        })
        .catch((error) => {
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$swal({
            icon: 'error',
            title: 'Oops',
            text: err,
            iconColor: this.$primaryColor,
            allowOutsideClick: false
          })
        })
        .finally(() => {
          this.showVerifyAuthDialog = false
        })
    },
    goBack () {
      this.$router.push('/settings')
    },
    closeDrawer (drawer) {
      this[drawer] = false
    },
    openDrawer (drawer) {
      this[drawer] = true
    }
  },
  components: {
    PlusIcon: () => import('@/assets/v2/icons/Plus.vue'),
    AssetIcon: () => import('@/assets/v2/icons/AssetIcon.vue'),
    WithdrawalAccountDrawer: () =>
      import(
        '@/components/v2/withdrawal/drawers/withdrawalAccountForm/index.vue'
      ),
    WithdrawalDetailsDrawer: () =>
      import(
        '@/components/v2/settings/drawers/withdrawalDetailsDrawer/Index.vue'
      ),
    DeleteIcon: () => import('@/assets/icons/DeleteIcon.vue'),
    EyeIcon: () => import('@/assets/icons/EyeIcon.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    AttentionDialog: () => import('@/components/v2/app/AttentionDialog.vue'),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    AccountsEmptyState: () =>
      import('@/components/empty-states/AccountsEmptyState.vue'),
    BeneficiariesLoader: () =>
      import('@/components/v2/skeletalLoaders/BeneficiariesLoader.vue')
  },
  computed: {
    noWithdrawalMethods () {
      return this.withdrawalMethods.length === 0
    },
    isFiatAccount () {
      return this.activeTab === this.$transactionType.BANK_TRANSFER
    },
    isAssetAccount () {
      return this.activeTab === this.$transactionType.ASSET_TRANSFER
    },
    withdrawalMethods () {
      return this.$store.state.withdrawal.withdrawalMethods
    },
    hasNoAssetTransfers () {
      return this.withdrawalMethods.some(
        (withdrawalMethod) =>
          withdrawalMethod.method === this.$transactionType.ASSET_TRANSFER
      )
    },
    hasNoBankTransfers () {
      return this.withdrawalMethods.some(
        (withdrawalMethod) =>
          withdrawalMethod.method === this.$transactionType.BANK_TRANSFER
      )
    },
    assetAccounts () {
      const assetAccounts = this.withdrawalMethods.filter(
        (item) => item.method === this.$transactionType.ASSET_TRANSFER
      )
      return assetAccounts || []
    },
    fiatAccounts () {
      const fiatAccounts = this.withdrawalMethods.filter(
        (item) => item.method === this.$transactionType.BANK_TRANSFER
      )
      return fiatAccounts || []
    },
    user () {
      return this.$store.state.auth.user
    },
    hasLimitedSettings () {
      return process.env.VUE_APP_SETTINGS_LIMITED === 'true'
    }
  },
  async mounted () {
    await this.getWithdrawalMethods()
  }
}
</script>
<style scoped>
.user-card {
  background: var(--background);
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.1),
    0px 4px 8px rgba(3, 2, 7, 0.1);
  border-radius: 15px;
}

.max-height {
  max-height: 60vh;
  overflow-y: scroll;
}

.border-bottom:not(:last-child) {
  border-bottom: 1px solid #d4d6e2;
}
</style>
